
import React, { useEffect, useState } from "react"
import { Grid, Box, Link, Text, Heading, Flex, Stack, AspectRatioBox } from "@chakra-ui/core"
import groupBy from "lodash/groupBy"
import dayjs from "dayjs"
import dayOfYear from "dayjs/plugin/dayOfYear"
import { range } from "d3-array"
import { csv } from "d3-fetch"

import Canton from "../Canton"
import Share from "../Share"

const areaTemplateMobile = `
"in in"
"BS BL"
"JU SO"
"AG ZH"
"SH TG"
"AR AI"
"LU ZG"
"FR NE"
"BE VS"
"SG GL"
"TI GR"
"GE VD"
"SZ UR"
"OW NW"
"ee oo"
`

const areaTemplate = `
"in in bb BS dd SH ee ff gg"
"in in JU BL AG ZH TG AR jj"
"in in NE SO LU ZG SG AI FL"
"nn VD FR BE NW SZ GL mm GR"
"GE oo VS pp OW UR qq rr ss"
"tt uu vv ww xx TI yy zz za"
`

const allCantons = [
  { name: "Aargau", id: "AG" },
  { name: "Appenzell IR", id: "AI" },
  { name: "Appenzell AR", id: "AR" },
  { name: "Berne", id: "BE" },
  { name: "Basel-Land", id: "BL" },
  { name: "Basel-Stadt", id: "BS" },
  { name: "Fribourg", id: "FR" },
  { name: "Geneva", id: "GE" },
  { name: "Glarus", id: "GL" },
  { name: "Grisons", id: "GR" },
  { name: "Jura", id: "JU" },
  { name: "Lucerne", id: "LU" },
  { name: "Neuchâtel", id: "NE" },
  { name: "Nidwalden", id: "NW" },
  { name: "Obwalden", id: "OW" },
  { name: "St. Gallen", id: "SG" },
  { name: "Schaffhausen", id: "SH" },
  { name: "Solothurn", id: "SO" },
  { name: "Schwyz", id: "SZ" },
  { name: "Thurgau", id: "TG" },
  { name: "Ticino", id: "TI" },
  { name: "Uri", id: "UR" },
  { name: "Vaud", id: "VD" },
  { name: "Valais", id: "VS" },
  { name: "Zug", id: "ZG" },
  { name: "Zürich", id: "ZH" },
]

dayjs.extend(dayOfYear)

const today = dayjs().dayOfYear()
const startDay = dayjs("2020-02-25").dayOfYear()
const dates = range(today - startDay + 1).map(d => {
  return dayjs("2020-02-25").add(d, "day").format("YYYY-MM-DD")
})

const BoxMap = () => {
  const [data, setData] = useState({
    metaData: {},
    cantons: [],
  })

  useEffect(() => {
    csv("https://raw.githubusercontent.com/openZH/covid_19/master/COVID19_Fallzahlen_CH_total.csv")
      .then(csvData => {
        const grouped = groupBy(csvData, o => o.abbreviation_canton_and_fl)
        let cumulativeMax = 0
        const cleaned = Object.keys(grouped).reduce((acc, cur) => {
          if (cur === "FL") return acc
          const max = grouped[cur].reduce((acc, cur) => +cur.ncumul_conf > acc ? +cur.ncumul_conf : acc, 0)
          cumulativeMax = cumulativeMax > max ? cumulativeMax : max
          return { ...acc, [cur]: grouped[cur].filter(d => d.ncumul_conf) }
        }, {})
        setData({
          metaData: { max: cumulativeMax },
          cantons: Object.keys(cleaned)
            .map(d => {
              const cantonMetaData = allCantons.find(s => s.id === d)
              return {
                ...cantonMetaData,
                data2: cleaned[d],
                data: dates.map(date => {
                  const o = cleaned[d].find(s => s.date === date)
                  return o || { date, ncumul_conf: "MISSING" }
                }),
              }
            }),
        })
      })
  }, [])

  return (
    <>

      <Grid gridTemplateColumns="repeat(9, minmax(0, 1fr))" alignItems="stretch" gridRowGap="2.5rem" gridColumnGap={["0.75rem", null, null, null, "1rem"]} py="2.5rem" px={["1rem", null, "1.25rem", null, "2.5rem"]}>
        <Stack gridColumn="span 9" spacing="1.25rem">
          <Flex justifyContent="space-between">
          <Heading as="h1" fontSize={["2rem", null, "2.5rem"]} lineHeight={["2.5rem", null, "2.75rem"]} letterSpacing="-0.03125rem">
            { "SARS-CoV-2 infections in Switzerland" }
          </Heading>
          <Share />
          </Flex>
          <Text maxWidth="45rem" fontWeight={500}>
            { "Made by " }
            <Link href="https://www.zcreativelabs.com" color="yellow.700" target="_blank" rel="noopener noreferrer">{ "z creative labs" }</Link>
            { " based on " }
            <Link href="https://github.com/openZH/covid_19" color="yellow.700" target="_blank" rel="noopener noreferrer">
              { "regularly updated open source data from the Specialist Unit for Open Government Data Canton of Zurich" }
            </Link>
            { " and inspired by " }
            <Link href="https://twitter.com/rastrau/status/1244287138713255936" color="yellow.700" target="_blank" rel="noopener noreferrer">
              { "Ralph Straumann's work" }
            </Link>
            { "." }
          </Text>
        </Stack>
        <Box gridColumn="span 2" bg="white" borderBottom="0.25rem solid" borderColor="yellow.600" />
        <Box gridColumn="span 7" bg="white" borderBottom="0.25rem solid" borderColor="gray.200" />
      </Grid>

      <Grid gridTemplateColumns={["1fr 1fr", null, null, "repeat(9, minmax(0, 1fr))"]} alignItems="end" gridTemplateAreas={[areaTemplateMobile, null, null, areaTemplate]} gridGap={["0.75rem", null, null, null, "1rem"]} px={["1rem", null, "1.25rem", null, "2.5rem"]} pb="2.5rem">

        <Stack gridArea="in" position="relative" alignSelf="start" spacing="1.5rem" pb="2rem">
          <Stack spacing="0.75rem">
            <Heading as="h2" fontSize="1.25rem" lineHeight="1.25rem">
              { "Confirmed cases" }
            </Heading>
            <Text fontSize="1rem" lineHeight="1.5rem" fontWeight={500}>
              { "Reported number of confirmed infections with SARS-CoV-2 by canton over time" }
              <Link color="yellow.700" href="#sars-cov-2-v-covid-19">{ "*" }</Link>
            </Text>
          </Stack>
          <Stack isInline spacing="0.75rem" alignItems="center">
            <Box width="2.5rem" height="0.125rem" bg="yellow.600" />
            <Text fontSize="0.875rem" lineHeight="1rem">{ "Confirmed cases" }</Text>
          </Stack>
          <Stack isInline spacing="0.75rem" alignItems="center">
            <Box width="2.5rem" height="0.125rem" bg="yellow.300" />
            <Text fontSize="0.875rem" lineHeight="1rem">{ "Missing data" }</Text>
          </Stack>
        </Stack>
        <Box gridArea="ee" bg="gray.200" display={["none", null, null, null, "block"]}>
          <AspectRatioBox ratio={2/1}>
            <Box bg="blue.50" />
          </AspectRatioBox>
        </Box>
        <Box gridArea="oo" bg="gray.200" alignSelf="start" display={["none", null, null, null, "block"]}>
          <AspectRatioBox ratio={2/1}>
            <Box bg="blue.50" />
          </AspectRatioBox>
        </Box>

        {
          data.cantons.length ?
          data.cantons.map(canton => {
            return (
              <Box key={canton.id} gridArea={canton.id}>
                <Canton canton={canton} metaData={data.metaData} />
              </Box>
            )
          })
          : <Box>{ "Loading map..." }</Box>
        }

      </Grid>

      <Grid
        gridTemplateColumns={["1fr 1fr", null, null, "repeat(9, minmax(0, 1fr))"]}
        alignItems="start"
        gridRowGap={["1.25rem", null, "2.5rem"]}
        gridColumnGap={["0.75rem", null, null, null, "1rem"]}
        py="2.5rem"
        px={["1rem", null, "1.25rem", null, "2.5rem"]}
      >

        <Box borderTop="0.25rem solid" borderColor="gray.200" gridColumn={["span 2", null, null, "span 9"]} />
        
        <Stack gridColumn={["span 2", null, "span 1", "span 3"]} spacing="1.25rem">
          <Text id="sars-cov-2-v-covid-19" fontWeight={500}>
             { "* The infection rates refer to the infection with " }
            <Link href="https://en.wikipedia.org/wiki/Severe_acute_respiratory_syndrome_coronavirus_2" color="yellow.700">
              { "SARS-CoV02" }
            </Link>
            { "whereas the disease caused by the virus is called " }
            <Link href="https://en.wikipedia.org/wiki/Coronavirus_disease_2019" color="yellow.700">
              { "Covid-19" }
            </Link>
            { "." }
          </Text>
        </Stack>
        <Stack gridColumn={["span 2", null, "span 1", "span 3"]} spacing="1.25rem">
          <Text fontWeight={500}>
            { "If you have concerns and issues with the data used in this chart, please check out the github repo first." }
          </Text>
          <Text fontWeight={500}>
            { "For questions about the chart, you can contact us at " }
            <Link href="" color="yellow.700">
              { "hello@zcreativelabs.com" }
            </Link>
            { "." }
          </Text>
        </Stack>
        <Box gridColumn={["span 2", null, "span 1", "span 3"]}>
          <Text textAlign={["left", null, null, null, "right"]} fontWeight={500}>
            { "Made by " }
            <Link href="https://www.zcreativelabs.com" target="_blank" rel="noopener noreferrer" color="yellow.700">
              { "z creative labs" }
            </Link>
            { "." }
          </Text>
        </Box>
      </Grid>
    </>
  )
}

export default BoxMap
